import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ArrowLeft, Send, Edit, Trash, Building } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import PartnerHeader from '../../components/admin/partner/PartnerHeader';
import PartnerInfo from '../../components/admin/partner/PartnerInfo';
import OffersList from '../../components/admin/partner/OffersList';
import BusinessPartnerFormModal from '../../components/admin/BusinessPartnerFormModal';
import BusinessOfferFormModal from '../../components/admin/BusinessOfferFormModal';
import BusinessOfferBookingForm from '../../components/admin/BusinessOfferBookingForm';
import type { Business, BusinessOffer } from '../../types/business';
import { GET_BUSINESS_PARTNER, GET_PARTNER_OFFERS } from '../../graphql/admin/queries/business';
import { gql } from '@apollo/client';

interface BusinessPartnerData {
  business: Business;
}

interface PartnerOffersData {
  businessOffers: BusinessOffer[];
}

const DELETE_BUSINESS_OFFER = gql`
  mutation DeleteBusinessOffer($id: Int!) {
    deleteBusinessOffer(id: $id) {
      id
    }
  }
`;

export default function BusinessPartnerDetailsPage() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<BusinessOffer | null>(null);
  const [selectedOfferForBooking, setSelectedOfferForBooking] = useState<number | null>(null);

  const { loading: partnerLoading, error: partnerError, data: partnerData } = useQuery<BusinessPartnerData>(
    GET_BUSINESS_PARTNER,
    {
      variables: { partnerId: parseInt(partnerId || '0', 10) },
      skip: !partnerId
    }
  );

  const { loading: offersLoading, error: offersError, data: offersData, refetch: refetchOffers } = useQuery<PartnerOffersData>(
    GET_PARTNER_OFFERS,
    {
      variables: { businessId: parseInt(partnerId || '0', 10) },
      skip: !partnerId,
      fetchPolicy: 'network-only'
    }
  );

  const [deleteBusinessOffer] = useMutation(DELETE_BUSINESS_OFFER, {
    onCompleted: () => {
      refetchOffers();
    },
    onError: (error) => {
      alert(`Erreur lors de la suppression : ${error.message}`);
    }
  });

  if (partnerLoading) return <div>Chargement des informations du partenaire...</div>;
  if (partnerError) return <div>Erreur: {partnerError.message}</div>;
  if (!partnerData?.business) return <div>Partenaire non trouvé</div>;

  const partner = partnerData.business;
  const offers = offersData?.businessOffers || [];

  const handleUpdateSuccess = (updatedPartner: Business) => {
    setShowEditModal(false);
  };

  const handleOfferSuccess = (newOffer: BusinessOffer) => {
    refetchOffers();
    setShowOfferModal(false);
    setSelectedOffer(null);
  };

  const handleAddTravelers = (offerId: number) => {
    setSelectedOfferForBooking(offerId);
  };

  const handleOfferAccepted = () => {
    refetchOffers();
  };

  const handleDeleteOffer = async (offerId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      try {
        await deleteBusinessOffer({
          variables: { id: offerId }
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleEditOffer = (offerId: number) => {
    const offer = offersData?.businessOffers.find(o => o.id === offerId);
    if (offer) {
      setSelectedOffer(offer);
      setShowOfferModal(true);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate('/admin/business-partners')}
            className="mr-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Retour
          </button>
          <h1 className="text-2xl font-bold">Détails du partenaire</h1>
        </div>

        <div className="mb-6 flex justify-end space-x-4">
            <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Edit className="mr-2 h-4 w-4" />
              Modifier
            </button>
            <button
            onClick={() => setShowOfferModal(true)}
            className="flex items-center rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700"
            >
            <Send className="mr-2 h-4 w-4" />
            Nouvelle offre
            </button>
        </div>

        <PartnerHeader partner={partner} />
        <PartnerInfo partner={partner} />
        <OffersList 
          offers={offers}
          onRefetch={refetchOffers}
          onOfferAccepted={handleOfferAccepted}
          onAddTravelers={handleAddTravelers}
          onDeleteOffer={handleDeleteOffer}
          onEditOffer={handleEditOffer}
          businessId={parseInt(partnerId || '0', 10)}
        />

        {showEditModal && (
        <BusinessPartnerFormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateSuccess}
          selectedBusiness={partner}
          title="Modifier l'entreprise"
        />
        )}

        {showOfferModal && (
        <BusinessOfferFormModal
          isOpen={showOfferModal}
          onClose={() => {
            setShowOfferModal(false);
            setSelectedOffer(null);
          }}
          business={partner}
          selectedOffer={selectedOffer}
          title={selectedOffer ? "Modifier l'offre" : "Nouvelle offre"}
        />
        )}

        {selectedOfferForBooking && (
          <BusinessOfferBookingForm
            isOpen={true}
            onClose={() => setSelectedOfferForBooking(null)}
            offerId={selectedOfferForBooking}
            onSubmit={(data) => {
              refetchOffers();
              setSelectedOfferForBooking(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
}